import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { WarningMessage } from '../../components/widgets';
import { ADMIN_EDITABLE_STATUSES, STATUSES, TRANSLATE_PREFIX } from '../consts';
import { buildErrorData, isDraftBlockingErrorKey } from '../validators';
import TabGroupedErrors from './TabGroupedErrors';

const toErrorEntry = (key, errVal, translate) =>
    typeof errVal === 'string'
        ? { key, messageKey: errVal }
        : { key, messageKey: errVal.messageKey, data: buildErrorData(errVal, translate) };

const FormActionWarnings = ({
    error,
    isBusy,
    isSaveDraftDisabled,
    readOnly,
    existingRequest,
    isLastTab,
    computedTabs,
    draftBlockedReasons,
    translate,
}) => {
    const { dirty, isValid, errors } = useFormikContext();
    const isAdminEditing = !!existingRequest && !readOnly && ADMIN_EDITABLE_STATUSES.includes(existingRequest.status);

    const hasAnyDraftBlocker = draftBlockedReasons.length > 0 || Object.keys(errors).some(isDraftBlockingErrorKey);

    const showDraftBlocked =
        !isBusy && isSaveDraftDisabled && !readOnly && (!existingRequest || existingRequest.status === STATUSES.DRAFT);

    const showSaveBlocked =
        isLastTab && !isBusy && !readOnly && isAdminEditing && (!dirty || !isValid || draftBlockedReasons.length > 0);

    const isSubmitBlocked = !isValid || draftBlockedReasons.length > 0;
    const showSubmitBlocked = isLastTab && !isBusy && !readOnly && !isAdminEditing && isSubmitBlocked;

    if (!error && !showDraftBlocked && !showSaveBlocked && !showSubmitBlocked) {
        return null;
    }

    // ``draftBlockedReasons`` carries reasons that aren't in Formik ``errors`` (URL validity).
    // Formik ``errors`` carries everything else (structural draft-blockers + submit-required misses).
    const reasonsForTab = tab =>
        draftBlockedReasons
            .filter(r => r.tabId === tab.id)
            .map((r, i) => ({
                key: `reason-${r.reasonKey}-${i}`,
                messageKey: r.reasonKey,
                data: buildErrorData(r, translate),
            }));
    const formikErrorsForTab = (tab, predicate = () => true) =>
        Object.entries(errors)
            .filter(([key]) => key.startsWith(`formData.${tab.id}.`) && predicate(key))
            .map(([key, errVal]) => toErrorEntry(key, errVal, translate));

    const getDraftBlockingTabErrors = tab => [
        ...reasonsForTab(tab),
        ...formikErrorsForTab(tab, isDraftBlockingErrorKey),
    ];
    const getCombinedTabErrors = tab => [...reasonsForTab(tab), ...formikErrorsForTab(tab)];

    return (
        <>
            {error && <WarningMessage type="error" message={<Translate id={error} />} />}
            {showDraftBlocked && (
                <WarningMessage
                    type="warning"
                    showIcon={false}
                    collapsible
                    collapsibleTitle={`${TRANSLATE_PREFIX}.ERRORS.DRAFT_BLOCKED_TITLE`}
                    message={
                        hasAnyDraftBlocker ? (
                            <TabGroupedErrors tabs={computedTabs} getTabErrors={getDraftBlockingTabErrors} />
                        ) : (
                            <Translate id={`${TRANSLATE_PREFIX}.ERRORS.NO_CHANGES`} />
                        )
                    }
                />
            )}
            {showSaveBlocked && (
                <WarningMessage
                    type="warning"
                    showIcon={false}
                    collapsible
                    collapsibleTitle={`${TRANSLATE_PREFIX}.ERRORS.SAVE_BLOCKED_TITLE`}
                    message={
                        isSubmitBlocked ? (
                            <TabGroupedErrors tabs={computedTabs} getTabErrors={getCombinedTabErrors} />
                        ) : (
                            <Translate id={`${TRANSLATE_PREFIX}.ERRORS.NO_CHANGES`} />
                        )
                    }
                />
            )}
            {showSubmitBlocked && (
                <WarningMessage
                    type="warning"
                    showIcon={false}
                    collapsible
                    collapsibleTitle={`${TRANSLATE_PREFIX}.ERRORS.SUBMIT_BLOCKED_TITLE`}
                    message={<TabGroupedErrors tabs={computedTabs} getTabErrors={getCombinedTabErrors} />}
                />
            )}
        </>
    );
};

FormActionWarnings.propTypes = {
    error: PropTypes.string,
    isBusy: PropTypes.bool.isRequired,
    isSaveDraftDisabled: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool.isRequired,
    existingRequest: PropTypes.shape({
        status: PropTypes.string,
    }),
    isLastTab: PropTypes.bool.isRequired,
    computedTabs: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
    draftBlockedReasons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(FormActionWarnings);
